/**
 * Global prompt modal — XSS-safe, accessible, returns Promise<string|null>.
 * Returns null if cancelled, string (possibly empty) if submitted.
 *
 * @param {{ title?: string, message?: string, defaultValue?: string, confirmLabel?: string, cancelLabel?: string }} options
 * @returns {Promise<string|null>}
 */
export function promptModal({
  title = 'Input',
  message = '',
  defaultValue = '',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel'
} = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const cleanup = (result) => {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    };

    // Overlay
    const overlay = document.createElement('div');
    overlay.className = 'g-modal-overlay';
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) cleanup(null);
    });

    // Dialog
    const dialog = document.createElement('div');
    dialog.className = 'g-modal';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'g-prompt-title');

    // Title
    const titleEl = document.createElement('h3');
    titleEl.id = 'g-prompt-title';
    titleEl.className = 'g-modal__title';
    titleEl.textContent = String(title);
    dialog.appendChild(titleEl);

    // Optional message
    if (message) {
      const msgEl = document.createElement('p');
      msgEl.className = 'g-modal__msg';
      msgEl.textContent = String(message);
      dialog.appendChild(msgEl);
    }

    // Input
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'g-modal__input';
    input.value = String(defaultValue);
    dialog.appendChild(input);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'g-modal__footer';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'g-modal__btn g-modal__btn--cancel';
    cancelBtn.textContent = String(cancelLabel);
    cancelBtn.onclick = () => cleanup(null);

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'g-modal__btn g-modal__btn--confirm';
    confirmBtn.textContent = String(confirmLabel);
    confirmBtn.onclick = () => cleanup(input.value);

    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);
    dialog.appendChild(footer);
    overlay.appendChild(dialog);

    const onKey = (e) => {
      if (e.key === 'Escape') cleanup(null);
      if (e.key === 'Enter') { e.preventDefault(); cleanup(input.value); }
    };
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);

    // Auto-focus input and select all for easy replacement
    requestAnimationFrame(() => {
      input.focus();
      input.select();
    });
  });
}
